import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  SEARCH_TAGS,
  SEARCH_MENTIONS,
} from "@/api/apollo-client/queries/Queries";
import { ADD_TAG } from "@/api/apollo-client/mutations/mutations";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useDebounce } from "@/hooks/useDebounce";
import Image from "next/image";
import defaultProfile from "@/images/default-profile.jpg";

interface SuggestionSearchProps {
  text: string;
  setText: (text: string) => void;
  onSelectMention: (mention: { id: string; value: string }) => void;
  onSelectTag: (tag: { id: string; value: string }) => void;
  backendText?: string;
  setBackendText: (text: string) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export enum SuggestionType {
  MENTION = "mention",
  TAG = "tag",
}

interface SuggestionItem {
  id: number | string;
  value: string;
  avatar?: string;
  isNew?: boolean;
}

interface MentionData {
  id: string;
  name: string;
  profile_picture?: {
    url: string;
  };
}

interface TagData {
  tag_id: string;
  tag_name: string;
}

const SuggestionSearch: React.FC<SuggestionSearchProps> = ({
  text,
  setText,
  onSelectMention,
  onSelectTag,
  backendText,
  setBackendText,
  textareaRef,
}) => {
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [suggestionType, setSuggestionType] = useState<SuggestionType | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [currentWordStart, setCurrentWordStart] = useState(0);
  const [currentWordEnd, setCurrentWordEnd] = useState(0);
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [fetchTags, { data: tagData }] = useLazyQuery(SEARCH_TAGS);
  const [fetchMentions, { data: mentionData }] = useLazyQuery(SEARCH_MENTIONS);
  const [addTag] = useMutation(ADD_TAG);

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Helper to get the correct textarea
  const getTextarea = () => textareaRef?.current;

  // Track cursor position changes from the textarea
  const handleCursorChange = (position: number) => {
    setCursorPosition(position);
    // Find the current word based on cursor position
    const beforeCursor = text.substring(0, position);
    const afterCursor = text.substring(position);
    // Find word boundaries
    const startSpacePos = beforeCursor.lastIndexOf(" ");
    const startNewlinePos = beforeCursor.lastIndexOf("\n");
    const startPos = Math.max(startSpacePos, startNewlinePos, -1) + 1;
    const endSpacePos = afterCursor.indexOf(" ");
    const endNewlinePos = afterCursor.indexOf("\n");
    const endPos =
      position +
      (endSpacePos >= 0 && endNewlinePos >= 0
        ? Math.min(endSpacePos, endNewlinePos)
        : endSpacePos >= 0
          ? endSpacePos
          : endNewlinePos >= 0
            ? endNewlinePos
            : afterCursor.length);
    setCurrentWordStart(startPos);
    setCurrentWordEnd(endPos);
    const currentWord = text.substring(startPos, endPos);
    // Only show one type of suggestion at a time
    if (currentWord.startsWith("@")) {
      setSuggestionType(SuggestionType.MENTION);
      setSearchTerm(currentWord.slice(1));
      setShowSuggestions(true);
    } else if (currentWord.startsWith("#")) {
      setSuggestionType(SuggestionType.TAG);
      setSearchTerm(currentWord.slice(1));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSuggestionType(null);
    }

    // Update dropdown position near the textarea caret (approx beneath textarea)
    const ta = getTextarea();
    if (ta) {
      const rect = (ta as HTMLTextAreaElement).getBoundingClientRect();
      setDropdownRect({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  };

  // Add a global event listener to monitor textarea cursor changes
  useEffect(() => {
    const textarea = getTextarea();
    if (!textarea) return;
    const handleSelect = () => {
      handleCursorChange((textarea as HTMLTextAreaElement).selectionStart);
    };
    textarea.addEventListener("click", handleSelect);
    textarea.addEventListener("keyup", handleSelect);
    textarea.addEventListener("select", handleSelect);
    return () => {
      textarea.removeEventListener("click", handleSelect);
      textarea.removeEventListener("keyup", handleSelect);
      textarea.removeEventListener("select", handleSelect);
    };
  }, [text, textareaRef]);

  // Initial setup
  useEffect(() => {
    const textarea = getTextarea();
    if (textarea) {
      handleCursorChange((textarea as HTMLTextAreaElement).selectionStart);
    }
  }, [textareaRef]);

  // Debounced fetch for suggestions
  useEffect(() => {
    if (!suggestionType) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (suggestionType === SuggestionType.MENTION) {
      fetchMentions({
        variables: { name_prefix: debouncedSearchTerm || "", first: 10 },
      });
    } else if (suggestionType === SuggestionType.TAG) {
      fetchTags({
        variables: { tag_name_prefix: debouncedSearchTerm || "", first: 10 },
      });
    }
  }, [debouncedSearchTerm, suggestionType, fetchMentions, fetchTags]);

  // Process backendText for mentions and tags
  useEffect(() => {
    if (!backendText) return;

    try {
      // Check if backendText is JSON format
      if (backendText.startsWith("{")) {
        const parsed = JSON.parse(backendText);
        const mentions: { id: string; value: string }[] = [];
        const tags: { id: string; value: string }[] = [];

        parsed.blocks.forEach((block: any) => {
          block.inlineStyleRanges?.forEach((range: any) => {
            if (range.style === "MENTION" && range.entity) {
              const entity = parsed.entityMap?.[range.entity];
              if (entity?.data?.mention) {
                const { id, name } = entity.data.mention;
                mentions.push({ id, value: name });
              }
            }
            if (range.style === "TAG" && range.entity) {
              const entity = parsed.entityMap?.[range.entity];
              if (entity?.data?.tag) {
                const { tag_id, tag_name } = entity.data.tag;
                tags.push({ id: tag_id, value: tag_name });
              }
            }
          });
        });

        // Notify parent once parsed
        mentions.forEach((m) => onSelectMention(m));
        tags.forEach((t) => onSelectTag(t));
      } else {
        // Process plain text with special formatting
        processTokenizedText(backendText);
      }
    } catch (err) {
      console.warn("Failed to parse backendText", err);
      processTokenizedText(backendText);
    }
  }, []);

  // Function to process text with special tokens
  const processTokenizedText = (text: string) => {
    if (!text) return;

    // Look for mention pattern: @[name](user:id)
    const mentionRegex = /@\[(.*?)\]\(user:(.*?)\)/g;
    let mentionMatch;

    while ((mentionMatch = mentionRegex.exec(text)) !== null) {
      const value = mentionMatch[1];
      const id = mentionMatch[2];
      onSelectMention({ id, value });
    }

    // Look for tag pattern: #[name](tag:id)
    const tagRegex = /#\[(.*?)\]\(tag:(.*?)\)/g;
    let tagMatch;

    while ((tagMatch = tagRegex.exec(text)) !== null) {
      const value = tagMatch[1];
      const id = tagMatch[2];
      onSelectTag({ id, value });
    }
  };

  useEffect(() => {
    const activeItem = itemRefs.current[activeIndex];
    if (activeItem) {
      activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (suggestionType === SuggestionType.MENTION && mentionData) {
      const results = mentionData.searchMentioned || [];
      const formatted = results.map((m: MentionData) => ({
        id: m.id,
        value: m.name,
        avatar: m.profile_picture?.url,
      }));
      setSuggestions(formatted);
      setShowSuggestions(true); // Always show suggestions dropdown
      setActiveIndex(0);
    }
  }, [mentionData, debouncedSearchTerm, suggestionType]);

  useEffect(() => {
    if (suggestionType === SuggestionType.TAG && tagData) {
      const results = tagData.searchTags || [];
      const formatted = results.map((tag: TagData) => ({
        id: tag.tag_id,
        value: tag.tag_name,
      }));

      const collator = new Intl.Collator("en", { sensitivity: "base" });
      const tagExists = formatted.some(
        (tag: { value: string }) =>
          collator.compare(tag.value, searchTerm) === 0
      );

      if (!tagExists && searchTerm) {
        formatted.push({
          id: "new",
          value: searchTerm,
          isNew: true,
        });
      }

      setSuggestions(formatted);
      setShowSuggestions(true); //suggestion dropdown visible
      setActiveIndex(0);
    }
  }, [tagData, debouncedSearchTerm, searchTerm, suggestionType]);

  const selectSuggestion = async (value: string, id: string | number) => {
    const prefix = suggestionType === SuggestionType.MENTION ? "@" : "#";

    if (suggestionType === SuggestionType.TAG && id === "new") {
      try {
        const res = await addTag({ variables: { tag_name: searchTerm } });
        const newTagId = res.data.addTag;

        const beforeWord = text.substring(0, currentWordStart);
        const afterWord = text.substring(currentWordEnd);
        const updatedText = beforeWord + prefix + searchTerm + " " + afterWord;

        setText(updatedText);

        onSelectTag({ id: String(newTagId), value: searchTerm });
        setShowSuggestions(false);
        setSearchTerm("");
        setSuggestionType(null);

        // Set cursor position after the inserted text
        setTimeout(() => {
          const textarea = getTextarea();
          if (textarea) {
            const newPosition =
              currentWordStart + prefix.length + searchTerm.length + 1;
            textarea.focus();
            textarea.setSelectionRange(newPosition, newPosition);
          }
        }, 0);

        return;
      } catch (err) {
        console.error("Failed to add tag", err);
        return;
      }
    }

    // Replace the current word in the display text
    const beforeWord = text.substring(0, currentWordStart);
    const afterWord = text.substring(currentWordEnd);
    const updatedText = beforeWord + prefix + value + " " + afterWord;

    setText(updatedText);

    setShowSuggestions(false);

    if (suggestionType === SuggestionType.MENTION) {
      onSelectMention({ id: String(id), value: value });
    } else if (suggestionType === SuggestionType.TAG) {
      onSelectTag({ id: String(id), value: value });
    }

    setSearchTerm("");
    setSuggestionType(null);

    // Set cursor position after the inserted text
    setTimeout(() => {
      const textarea = getTextarea();
      if (textarea) {
        const newPosition = currentWordStart + prefix.length + value.length + 1;
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!showSuggestions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selected = suggestions[activeIndex];
      if (selected) selectSuggestion(selected.value, selected.id);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    const textarea = getTextarea();
    if (!textarea) return;
    const keydownHandler = (e: any) => handleKeyDown(e);
    textarea.addEventListener("keydown", keydownHandler);
    return () => textarea.removeEventListener("keydown", keydownHandler);
  }, [showSuggestions, suggestions, activeIndex, textareaRef]);

  const highlightMatch = (text: string, query: string) => {
    const i = text.toLowerCase().indexOf(query.toLowerCase());
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <span className="font-bold text-blue-500">
          {text.slice(i, i + query.length)}
        </span>
        {text.slice(i + query.length)}
      </>
    );
  };

  return (
    <div className="relative">
      {showSuggestions &&
        dropdownRect &&
        ReactDOM.createPortal(
          <div
            className="fixed rounded-md shadow-md z-[9999] bg-white text-black dark:bg-gray-800 dark:text-white border border-gray-300 dark:border-gray-700 max-h-64 overflow-y-auto"
            style={{
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {suggestions.length === 0 ? (
              <div className="p-2 text-gray-500 text-center">
                No suggestions
              </div>
            ) : (
              suggestions.map((item, index) => (
                <div
                  key={item.id}
                  ref={(el) => {
                    if (el) {
                      itemRefs.current[index] = el;
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    selectSuggestion(item.value, item.id);
                  }}
                  className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    index === activeIndex ? "bg-gray-200 dark:bg-gray-700" : ""
                  }`}
                >
                  {suggestionType === SuggestionType.MENTION && (
                    <Image
                      src={item.avatar || defaultProfile}
                      alt={item.value || ""}
                      width={200}
                      height={200}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  )}
                  <span>
                    {suggestionType === SuggestionType.MENTION ? "@" : "#"}
                    {item.isNew ? (
                      <>
                        <span>{highlightMatch(item.value, searchTerm)}</span>
                        <span className="text-gray-500 ml-1">(new tag)</span>
                      </>
                    ) : (
                      highlightMatch(item.value, searchTerm)
                    )}
                  </span>
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SuggestionSearch;
